import CompanyDetailsInfo from '@/components/companies/CompanyDetailsInfo';
import CompanyInitialDepositInfo from '@/components/companies/CompanyIntitalDepositInfo';
import CompanyInterestRateInfo from '@/components/companies/CompanyInterestRateInfo';
import { SVButton, SVLoader } from '@/components/core';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import { ROUTES } from '@/constants/routes';
import { useGetCompanyDetails } from '@/hooks/requests/useCompanies';
import { useFetchProgramSettings } from '@/hooks/requests/useSettings';
import { Card, Grid, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import CompanyFees from '@/components/companies/CompanyFees';
import CompanyPermissions from '@/components/companies/CompanyPermissions';
import CompanyBrandingThemes from '@/components/companies/CompanyBrandingThemes';
import CompanyDetailsView from '@/components/companies/CompanyDetailsView';
import CompanyDetailsActions from '@/components/companies/CompanyDetailsActions';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/types/companies';

export default function CompanyDetailsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uuid = '' } = useParams();
  const { companyDetails, isCompanyDetailsLoading, isError, error } =
    useGetCompanyDetails(uuid);
  const { data: programSettings, isLoading: isProgramSettingsLoading } =
    useFetchProgramSettings();

  useEffect(() => {
    if (uuid) {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.details, uuid],
      });
    }
  }, [uuid, queryClient]);

  if (isCompanyDetailsLoading || isProgramSettingsLoading) {
    return (
      <DefaultLayout header="">
        <Card>
          <SVLoader />
        </Card>
      </DefaultLayout>
    );
  }

  if (!companyDetails || !programSettings) {
    return (
      <DefaultLayout header="">
        <Card>
          <Typography>
            {isError
              ? `Error loading company details: ${error?.message || 'Unknown error'}`
              : 'Something went wrong retrieving the company. Our team has been notified.'}
          </Typography>
          <SVButton
            onClick={() => navigate(ROUTES.COMPANIES)}
            variant="primary"
            sx={{ mt: 2 }}>
            Back to Companies
          </SVButton>
        </Card>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout
      header={`${companyDetails.name} Details`}
      buttons={[
        <SVButton
          onClick={() =>
            navigate(`${ROUTES.COMPANIES_EDIT_PARAMS}/${companyDetails.uuid}`)
          }
          fullWidth
          key="edit"
          aria-label="Edit Company">
          Edit
        </SVButton>,
      ]}
      breadcrumb={[
        { label: 'Companies', href: ROUTES.COMPANIES },
        { label: companyDetails.name, isActive: true },
      ]}>
      <Stack spacing={2}>
        <CompanyDetailsInfo companyDetails={companyDetails} />
        <CompanyInterestRateInfo companyDetails={companyDetails} />
        <CompanyInitialDepositInfo
          initialDepositAmount={
            companyDetails.minimumInitDeposit
              ? parseFloat(companyDetails.minimumInitDeposit.value.toString())
              : 0
          }
        />
        <CompanyFees
          companyDetails={companyDetails}
          programSettings={programSettings}
        />
        <CompanyPermissions companyDetails={companyDetails} />
        <CompanyBrandingThemes companyDetails={companyDetails} />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <CompanyDetailsView companyDetails={companyDetails} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <CompanyDetailsActions companyDetails={companyDetails} />
          </Grid>
        </Grid>
      </Stack>
    </DefaultLayout>
  );
}
